/**
 * Sprint 4H-DEP.B — DepositoPicker
 *
 * Combobox per selezionare un deposito nella scheda veicolo.
 * - GRUPPO 1: depositi FISICI della sede (sede_id == site_id passata)
 * - GRUPPO 2: 2 depositi di SISTEMA (globali, sempre disponibili)
 *
 * Props:
 *   - value          : id del deposito attualmente selezionato (string | null)
 *   - onChange(id)   : callback al cambio selezione
 *   - siteId         : id sede del veicolo (filtra i fisici)
 *   - disabled       : disabilita il picker
 *   - testIdPrefix   : prefisso data-testid (default "deposito-picker")
 *
 * Se siteId è null → picker disabilitato con hint "Seleziona prima la sede".
 * Veicoli legacy con deposito_id null → mostra "— Da assegnare —".
 */
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent,
  SelectItem, SelectGroup, SelectLabel, SelectSeparator,
} from "@/components/ui/select";

export default function DepositoPicker({
  value,
  onChange,
  siteId,
  disabled = false,
  testIdPrefix = "deposito-picker",
  label = "Deposito",
  onLoad,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!siteId) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const r = await api.get(
          `/depositi?sede_id=${siteId}&include_sistema=true`);
        if (!cancelled) {
          setItems(r.data || []);
          if (onLoad) onLoad(r.data || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [siteId]);

  const { fisici, sistema } = useMemo(() => {
    const f = items.filter((d) => d.tipo === "FISICO");
    const s = items.filter((d) => d.tipo === "SISTEMA");
    return { fisici: f, sistema: s };
  }, [items]);

  const isDisabled = disabled || !siteId;

  return (
    <div data-testid={testIdPrefix}>
      {label && <Label className="text-xs">{label}</Label>}
      <Select
        value={value || ""}
        onValueChange={(v) => onChange(v || null)}
        disabled={isDisabled}
      >
        <SelectTrigger
          className="h-9 text-sm"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <SelectValue placeholder={
            !siteId ? "Seleziona prima la sede" :
            loading ? "Caricamento…" :
            value ? "" : "— Seleziona deposito —"
          } />
        </SelectTrigger>
        <SelectContent>
          {fisici.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                Depositi della sede
              </SelectLabel>
              {fisici.map((d) => (
                <SelectItem key={d.id} value={d.id}
                            data-testid={`${testIdPrefix}-opt-${d.id}`}>
                  {d.nome}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          {sistema.length > 0 && (
            <>
              {fisici.length > 0 && <SelectSeparator />}
              <SelectGroup>
                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                  Depositi di sistema
                </SelectLabel>
                {sistema.map((d) => (
                  <SelectItem key={d.id} value={d.id}
                              data-testid={`${testIdPrefix}-opt-sys-${d.codice_sistema}`}>
                    {d.nome}
                  </SelectItem>
                ))}
              </SelectGroup>
            </>
          )}
        </SelectContent>
      </Select>
      {!siteId && (
        <p className="text-[11px] text-slate-500 mt-1">
          Seleziona prima la sede per visualizzare i depositi disponibili.
        </p>
      )}
    </div>
  );
}
